import React, { useState, useEffect } from 'react';
import './TimetableGrid.css';
import TeacherAssignmentsService from '../../services/TeacherAssignmentsService';
import RoomsService from '../../services/RoomsService';

const TimetableGrid = ({ data, title, hasElectives = false, timeSlots, dayNames, dayKeys, onCellEdit, editable = false, tenantId = 'eadcd263-a399-4261-8cbd-8fb1ce6da0e3' }) => {
  const [textSize, setTextSize] = useState('normal'); // 'small', 'normal', 'large', 'xlarge'
  const [editingCell, setEditingCell] = useState(null); // { day, period, data }
  const [editForm, setEditForm] = useState({});
  const [teachers, setTeachers] = useState([]);
  const [rooms, setRooms] = useState([]);
  const [teacherSearch, setTeacherSearch] = useState('');
  const [roomSearch, setRoomSearch] = useState('');
  const [showTeacherDropdown, setShowTeacherDropdown] = useState(false);
  const [showRoomDropdown, setShowRoomDropdown] = useState(false);
  const [loading, setLoading] = useState(false);
  const [showNodeInfo, setShowNodeInfo] = useState(false);
  
  // Default time slots for 15-period structure (8:00-3:50)
  const defaultTimeSlots = [
    '8:00-8:10',  // 班主任節
    '8:10-8:45',  // Period 1
    '8:45-9:20',  // Period 2
    '9:20-9:55',  // Period 3
    '9:55-10:15', // Recess
    '10:15-10:50', // Period 4
    '10:50-11:25', // Period 5
    '11:25-11:30', // Recess
    '11:30-12:05', // Period 6
    '12:05-12:40', // Period 7
    '12:40-1:50',  // Lunch
    '1:50-2:25',   // Period 8
    '2:25-3:00',   // Period 9
    '3:00-3:35',   // Period 10
    '3:35-3:50'    // 班主任節
  ];
  
  // Default day names for 8-column structure (Time + 7 days)
  const defaultDayNames = ['時間', '星期一', '星期二', '星期三', '星期四', '星期五', '星期六', '星期日'];
  const defaultDayKeys = ['time', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];
  
  // Use provided props or defaults
  const actualTimeSlots = timeSlots || defaultTimeSlots;
  const actualDayNames = dayNames || defaultDayNames;
  const actualDayKeys = dayKeys || defaultDayKeys;
  
  const textSizeOptions = [
    { key: 'small', label: '小', icon: 'A' },
    { key: 'normal', label: '中', icon: 'A' },
    { key: 'large', label: '大', icon: 'A' },
    { key: 'xlarge', label: '特大', icon: 'A' }
  ];
  
  const handleTextSizeChange = (size) => {
    setTextSize(size);
  };

  // Load teachers and rooms data
  useEffect(() => {
    const loadData = async () => {
      if (!editable) return;
      
      setLoading(true);
      try {
        const [teachersData, roomsData] = await Promise.all([
          TeacherAssignmentsService.getAllTeachers(tenantId),
          RoomsService.getAllRooms(tenantId)
        ]);
        
        setTeachers(teachersData);
        setRooms(roomsData);
      } catch (error) {
        console.error('Error loading data:', error);
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [editable, tenantId]);

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showTeacherDropdown && !event.target.closest('.teacher-dropdown')) {
        setShowTeacherDropdown(false);
      }
      if (showRoomDropdown && !event.target.closest('.room-dropdown')) {
        setShowRoomDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showTeacherDropdown, showRoomDropdown]);

  // Edit functionality
  const handleCellClick = (day, period, cellData) => {
    if (!editable) return;
    
    setEditingCell({ day, period, data: cellData });
    setEditForm({
      subject: cellData?.subject || '',
      teacher: cellData?.teacher || '',
      classroom: cellData?.classroom || '',
      class: cellData?.class || '',
      period_sequence: cellData?.period_sequence || period + 1,
      period_node: cellData?.period_node || `Period ${period + 1}`,
      period_number: cellData?.period_number || period + 1
    });
    
    // Initialize search fields
    setTeacherSearch(cellData?.teacher || '');
    setRoomSearch(cellData?.classroom || '');
    setShowTeacherDropdown(false);
    setShowRoomDropdown(false);
  };

  const handleEditFormChange = (field, value) => {
    setEditForm(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSaveEdit = () => {
    if (onCellEdit && editingCell) {
      onCellEdit(editingCell.day, editingCell.period, editForm);
    }
    setEditingCell(null);
    setEditForm({});
  };

  const handleCancelEdit = () => {
    setEditingCell(null);
    setEditForm({});
    setTeacherSearch('');
    setRoomSearch('');
    setShowTeacherDropdown(false);
    setShowRoomDropdown(false);
  };

  // Filter teachers based on search
  const filteredTeachers = teachers.filter(teacher => 
    teacher.name.toLowerCase().includes(teacherSearch.toLowerCase()) ||
    teacher.code.toLowerCase().includes(teacherSearch.toLowerCase()) ||
    (teacher.english_name && teacher.english_name.toLowerCase().includes(teacherSearch.toLowerCase()))
  );

  // Filter rooms based on search
  const filteredRooms = rooms.filter(room => 
    room.name.toLowerCase().includes(roomSearch.toLowerCase()) ||
    room.code.toLowerCase().includes(roomSearch.toLowerCase())
  );

  // Handle teacher selection
  const handleTeacherSelect = (teacher) => {
    setEditForm(prev => ({
      ...prev,
      teacher: teacher.name
    }));
    setTeacherSearch(teacher.name);
    setShowTeacherDropdown(false);
  };

  // Handle room selection
  const handleRoomSelect = (room) => {
    setEditForm(prev => ({
      ...prev,
      classroom: room.name
    }));
    setRoomSearch(room.name);
    setShowRoomDropdown(false);
  };
  
  // Debug logging
  console.log('🎯 TimetableGrid received data:', data);
  console.log('📊 TimetableGrid data type:', typeof data);
  console.log('🔑 TimetableGrid data keys:', data ? Object.keys(data) : 'null');
  
  // Additional debugging for data structure
  if (data && data.monday) {
    console.log('📅 Monday data length:', data.monday.length);
    console.log('📅 Monday data sample:', data.monday.slice(0, 3));
  }
  
  // Validate data structure
  const validateData = (data) => {
    if (!data) return false;
    const requiredDays = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday'];
    return requiredDays.every(day => Array.isArray(data[day]));
  };
  
  const isValidData = validateData(data);
  if (!isValidData) {
    console.warn('⚠️ Invalid data structure passed to TimetableGrid:', data);
    return (
      <div className="timetable-grid-container">
        <div className="text-center p-8 text-gray-500">
          <h3>No Timetable Data</h3>
          <p>Please load timetable data first.</p>
        </div>
      </div>
    );
  }
  
  // Check if it's mock data
  if (data && data.monday && data.monday.length > 0) {
    const firstPeriod = data.monday[0];
    console.log('🕐 First period data:', firstPeriod);
    
    if (firstPeriod && firstPeriod.subject === '中文' && firstPeriod.teacher === '王老師' && firstPeriod.classroom === 'A101') {
      console.log('⚠️  MOCK DATA DETECTED in TimetableGrid!');
    } else if (firstPeriod) {
      console.log('✅ REAL DATA DETECTED in TimetableGrid!');
    } else {
      console.log('📝 EMPTY PERIOD DETECTED in TimetableGrid!');
    }
  }
  
  const renderScheduleCell = (schedule, dayKey, index) => {
    const cellClickable = editable ? 'cursor-pointer hover:bg-gray-50' : '';
    
    if (!schedule) {
      return (
        <td key={dayKey}>
          <div 
            className={`timetable-cell timetable-empty-cell ${cellClickable}`}
            onClick={() => handleCellClick(dayKey, index, null)}
          >
            <div className="timetable-empty-text">
              {/* Empty - no content */}
            </div>
          </div>
        </td>
      );
    }

    // Handle breaks (recess, lunch)
    if (schedule.type === 'break' || (schedule.subject && (schedule.subject === 'Recess' || schedule.subject === 'Lunch'))) {
      return (
        <td key={dayKey}>
          <div 
            className={`timetable-cell timetable-break-cell h-full flex items-center justify-center ${cellClickable}`}
            onClick={() => handleCellClick(dayKey, index, schedule)}
          >
            <div className="timetable-break-text">
              {schedule.subject || 'Break'}
            </div>
          </div>
        </td>
      );
    }

    // Handle master class periods
    if (schedule.type === 'master' || (schedule.subject && schedule.subject === 'Master Class Period')) {
      return (
        <td key={dayKey}>
          <div className="timetable-cell timetable-master-cell h-full flex items-center justify-center">
            <div className="timetable-master-text">
              {schedule.subject || 'Master Class'}
            </div>
          </div>
        </td>
      );
    }

    // Handle special periods (cross-class self-study, etc.)
    if (schedule.type === 'special') {
      return (
        <td key={dayKey}>
          <div className="timetable-cell timetable-special-cell">
            <div className="timetable-special-badge">
              {schedule.subject || 'Special'}
            </div>
            {schedule.teacher && (
              <div className="timetable-teacher-text">
                {schedule.teacher}
              </div>
            )}
            {schedule.classroom && (
              <div className="timetable-classroom-text">
                {schedule.classroom}
              </div>
            )}
          </div>
        </td>
      );
    }

    // Handle electives with multiple options (X1, X2)
    if (schedule.type === 'elective' && schedule.electives) {
      return (
        <td key={dayKey}>
          <div className="timetable-cell">
            <div className="timetable-elective-badge">
              {schedule.subject || 'Elective'}
            </div>
            <div className="space-y-1">
              {schedule.electives.map((elective, idx) => (
                <div key={idx} className="timetable-elective-option">
                  <div className="timetable-subject-text">
                    {elective.subject}
                  </div>
                  {elective.classroom && (
                    <div className="timetable-classroom-text">
                      {elective.classroom}
                    </div>
                  )}
                  {elective.teacher && (
                    <div className="timetable-teacher-text">
                      {elective.teacher}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </td>
      );
    }

    // Handle multiple subjects (legacy format)
    if (schedule.type === 'multiple') {
      return (
        <td key={dayKey}>
          <div className="timetable-cell">
            <div className="timetable-elective-badge">
              {schedule.title}
            </div>
            <div className="space-y-1">
              {schedule.subjects.map((subj, idx) => (
                <div key={idx} className="timetable-elective-option">
                  <div className="timetable-subject-text">
                    {subj.subject}
                  </div>
                  {subj.classroom && (
                    <div className="timetable-classroom-text">
                      {subj.classroom}
                    </div>
                  )}
                  {subj.teacher && (
                    <div className="timetable-teacher-text">
                      {subj.teacher}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </td>
      );
    }

    // Handle empty periods
    if (!schedule.subject || schedule.subject === 'Empty' || schedule.subject === '' || schedule.type === 'empty') {
      return (
        <td key={dayKey}>
          <div className="timetable-cell timetable-empty-cell">
            <div className="timetable-empty-text">
              {/* Empty - no content */}
            </div>
          </div>
        </td>
      );
    }

    // Determine subject type for styling
    const subjectType = schedule.subject_type || schedule.type || 'core';
    const isCoreSubject = subjectType === 'core';
    const isAdminSubject = subjectType === 'admin';
    const isElectiveX1 = subjectType === 'elective' && schedule.elective_group && schedule.elective_group.includes('X1');
    const isElectiveX2 = subjectType === 'elective' && schedule.elective_group && schedule.elective_group.includes('X2');

    // Debug logging for period information
    if (schedule.period_sequence || schedule.period_node) {
      console.log('📅 Period info found:', {
        period_sequence: schedule.period_sequence,
        period_node: schedule.period_node,
        period_number: schedule.period_number,
        subject: schedule.subject
      });
    }

    return (
      <td key={dayKey}>
        <div 
          className={`timetable-cell ${cellClickable} ${
            isAdminSubject ? 'timetable-admin-cell' : 
            isElectiveX1 ? 'timetable-elective-x1-cell' :
            isElectiveX2 ? 'timetable-elective-x2-cell' : ''
          }`}
          onClick={() => handleCellClick(dayKey, index, schedule)}
        >
          {isCoreSubject && (
            <div className="timetable-core-badge">
              核心科
            </div>
          )}
          {isAdminSubject && (
            <div className="timetable-admin-badge">
              管理
            </div>
          )}
          {isElectiveX1 && (
            <div className="timetable-elective-x1-badge">
              X1選修
            </div>
          )}
          {isElectiveX2 && (
            <div className="timetable-elective-x2-badge">
              X2選修
            </div>
          )}
          <div className="timetable-subject-text">
            {schedule.class_id && `${schedule.class_id} `}{schedule.subject || 'Unknown Subject'}
          </div>
          {schedule.classroom && (
            <div className="timetable-classroom-text">
              {schedule.classroom}
            </div>
          )}
          {schedule.teacher && (
            <div className="timetable-teacher-text">
              {schedule.teacher}
            </div>
          )}
          {/* Display period information when showNodeInfo is true */}
          {showNodeInfo && (schedule.period_sequence || schedule.period_node) && (
            <div className="timetable-period-info text-xs text-gray-500 mt-1">
              {schedule.period_node && (
                <div>節點: {schedule.period_node}</div>
              )}
              {schedule.period_sequence && (
                <div>序列: {schedule.period_sequence}</div>
              )}
            </div>
          )}
        </div>
      </td>
    );
  };

  return (
    <div className="timetable-grid-container">
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-2xl font-bold text-gray-800">{title}</h2>
        <div className="flex items-center space-x-4">
          {/* Text Size Control */}
          <div className="flex items-center space-x-2">
            <span className="text-sm text-gray-600 font-medium">字體大小:</span>
            <div className="flex space-x-1">
              {textSizeOptions.map((option) => (
                <button
                  key={option.key}
                  onClick={() => handleTextSizeChange(option.key)}
                  className={`px-3 py-1 rounded-md text-sm font-medium transition-all duration-200 ${
                    textSize === option.key
                      ? 'bg-blue-500 text-white shadow-md'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                  title={`字體大小: ${option.label}`}
                >
                  <span className={`${textSize === option.key ? 'font-bold' : 'font-normal'}`}>
                    {option.icon}
                  </span>
                  <span className="ml-1 text-xs">{option.label}</span>
                </button>
              ))}
            </div>
          </div>
          
          {/* Show Node Info Toggle */}
          <div className="flex items-center space-x-2">
            <button
              onClick={() => setShowNodeInfo(!showNodeInfo)}
              className={`px-4 py-2 rounded-md text-sm font-medium transition-all duration-200 flex items-center space-x-2 ${
                showNodeInfo
                  ? 'bg-green-500 text-white shadow-md hover:bg-green-600'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
              title={showNodeInfo ? '隱藏節點信息' : '顯示節點信息'}
            >
              <span className="text-xs">🔍</span>
              <span>{showNodeInfo ? '隱藏節點' : '顯示節點'}</span>
            </button>
          </div>
          
          {hasElectives && (
            <div className="flex space-x-2 text-xs">
              <span className="bg-blue-100 text-blue-800 px-3 py-1 rounded-full font-semibold">核心科目</span>
              <span className="bg-purple-100 text-purple-800 px-3 py-1 rounded-full font-semibold">選修科目</span>
            </div>
          )}
        </div>
      </div>
      
      <div className="overflow-x-auto">
        <table className={`timetable-table timetable-text-${textSize}`}>
          <thead>
            <tr>
              <th>時間</th>
              {actualDayNames.map(day => (
                <th key={day} className="min-w-48">
                  {day}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {actualTimeSlots.map((timeSlot, index) => {
              // Calculate the period_sequence for this row (1-based)
              const periodSequence = index + 1;
              
              return (
                <tr key={timeSlot}>
                  <td className="timetable-time-cell">
                    <div className="time-slot-container">
                      <div className="time-slot">{timeSlot}</div>
                      <div className="period-info">
                        <div 
                          className="period-badge"
                          data-period-type={
                            periodSequence === 1 || periodSequence === 15 ? 'homeroom' :
                            periodSequence === 5 || periodSequence === 8 ? 'recess' :
                            periodSequence === 11 ? 'lunch' : 'period'
                          }
                        >
                          {periodSequence === 1 && '班主任節'}
                          {periodSequence === 2 && 'Period 1'}
                          {periodSequence === 3 && 'Period 2'}
                          {periodSequence === 4 && 'Period 3'}
                          {periodSequence === 5 && 'Recess'}
                          {periodSequence === 6 && 'Period 4'}
                          {periodSequence === 7 && 'Period 5'}
                          {periodSequence === 8 && 'Recess'}
                          {periodSequence === 9 && 'Period 6'}
                          {periodSequence === 10 && 'Period 7'}
                          {periodSequence === 11 && 'Lunch'}
                          {periodSequence === 12 && 'Period 8'}
                          {periodSequence === 13 && 'Period 9'}
                          {periodSequence === 14 && 'Period 10'}
                          {periodSequence === 15 && '班主任節'}
                        </div>
                        <div className="period-number">
                          {periodSequence}
                        </div>
                      </div>
                    </div>
                  </td>
                  {actualDayKeys.map(dayKey => {
                    // Get the schedule entry for this period_sequence (using array index)
                    const schedule = data[dayKey]?.[index];
                    return renderScheduleCell(schedule, dayKey, index);
                  })}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      
      {hasElectives && (
        <div className="timetable-explanation">
          <h4>📚 選修科目說明</h4>
          <div className="text-sm space-y-2">
            <div className="timetable-explanation-item">
              <span className="timetable-explanation-label">X1 選修:</span>
              <span className="timetable-explanation-text">學生需從生物、化學、物理、歷史、地理中選擇一科</span>
            </div>
            <div className="timetable-explanation-item">
              <span className="timetable-explanation-label">X2 選修:</span>
              <span className="timetable-explanation-text">學生需從生物、化學、物理、歷史、數學延伸中選擇一科</span>
            </div>
            <div className="timetable-explanation-item">
              <span className="timetable-explanation-label">跨班制:</span>
              <span className="timetable-explanation-text">同年級學生根據選修科目到不同教室上課</span>
            </div>
          </div>
        </div>
      )}

      {/* Edit Modal */}
      {editingCell && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-lg mx-4 max-h-[90vh] overflow-y-auto">
            <h3 className="text-lg font-semibold mb-4">編輯時間表項目</h3>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  科目
                </label>
                <input
                  type="text"
                  value={editForm.subject}
                  onChange={(e) => handleEditFormChange('subject', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="輸入科目名稱"
                />
              </div>

              <div className="relative">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  教師
                </label>
                <div className="relative teacher-dropdown">
                  <input
                    type="text"
                    value={teacherSearch}
                    onChange={(e) => {
                      setTeacherSearch(e.target.value);
                      setShowTeacherDropdown(true);
                    }}
                    onFocus={() => setShowTeacherDropdown(true)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="搜尋或輸入教師姓名"
                  />
                  {showTeacherDropdown && (
                    <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg max-h-60 overflow-y-auto">
                      {loading ? (
                        <div className="px-3 py-2 text-gray-500">載入中...</div>
                      ) : filteredTeachers.length > 0 ? (
                        filteredTeachers.map((teacher) => (
                          <div
                            key={teacher.id}
                            onClick={() => handleTeacherSelect(teacher)}
                            className="px-3 py-2 hover:bg-gray-100 cursor-pointer border-b border-gray-100 last:border-b-0"
                          >
                            <div className="font-medium">{teacher.name}</div>
                            <div className="text-sm text-gray-500">
                              {teacher.code} {teacher.english_name && `• ${teacher.english_name}`}
                            </div>
                          </div>
                        ))
                      ) : (
                        <div className="px-3 py-2 text-gray-500">找不到教師</div>
                      )}
                    </div>
                  )}
                </div>
              </div>

              <div className="relative">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  課室
                </label>
                <div className="relative room-dropdown">
                  <input
                    type="text"
                    value={roomSearch}
                    onChange={(e) => {
                      setRoomSearch(e.target.value);
                      setShowRoomDropdown(true);
                    }}
                    onFocus={() => setShowRoomDropdown(true)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="搜尋或輸入課室號碼"
                  />
                  {showRoomDropdown && (
                    <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg max-h-60 overflow-y-auto">
                      {loading ? (
                        <div className="px-3 py-2 text-gray-500">載入中...</div>
                      ) : filteredRooms.length > 0 ? (
                        filteredRooms.map((room) => (
                          <div
                            key={room.id}
                            onClick={() => handleRoomSelect(room)}
                            className="px-3 py-2 hover:bg-gray-100 cursor-pointer border-b border-gray-100 last:border-b-0"
                          >
                            <div className="font-medium">{room.name}</div>
                          </div>
                        ))
                      ) : (
                        <div className="px-3 py-2 text-gray-500">找不到課室</div>
                      )}
                    </div>
                  )}
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  班別
                </label>
                <input
                  type="text"
                  value={editForm.class}
                  onChange={(e) => handleEditFormChange('class', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="輸入班別"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    節點
                  </label>
                  <input
                    type="text"
                    value={editForm.period_node}
                    onChange={(e) => handleEditFormChange('period_node', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Period 1"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    序列
                  </label>
                  <input
                    type="number"
                    value={editForm.period_sequence}
                    onChange={(e) => handleEditFormChange('period_sequence', parseInt(e.target.value))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    min="1"
                    max="15"
                  />
                </div>
              </div>
            </div>

            <div className="flex justify-end space-x-3 mt-6">
              <button
                onClick={handleCancelEdit}
                className="px-4 py-2 text-gray-600 bg-gray-100 rounded-md hover:bg-gray-200 transition-colors"
              >
                取消
              </button>
              <button
                onClick={handleSaveEdit}
                className="px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600 transition-colors"
              >
                保存
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TimetableGrid;
